import { create } from "zustand";
import { stringify } from "qs";
import * as Yup from "yup";
import { validate as validateUUID } from "uuid";
import { AxiosError } from "axios";

import {
  EpochDeviceSplit,
  EpochDetails,
  EpochDetailsPayload,
  EpochRewardRecord,
  EpochSupplierPayoutRecord,
  EpochTransactionRecord,
  EpochDetailsResponse,
  EpochResponseType,
  EpochStatus,
  EpochSummaryResponseType,
  EpochSummaryType,
  EpochType,
  EpochsApiResponseType,
  EpochsDataResponseType,
  EpochsFetchFilters,
  EpochsFetchRequestType,
  EpochsFetchResponseType,
  EpochsStoreState
} from "@/types";
import { executeAPIRequest, toFriendlyError } from "@/utils/api";
import { sanitiseDeviceCount, toFiniteNumber } from "@/utils/deviceClassSplit";

const EPOCH_ID_REGEX = /^\d{4}-\d{2}-\d{2}-\d{2}$/;

const DEFAULT_FILTERS: EpochsFetchFilters = {
  epochFrom: "",
  epochTo: "",
  supplierId: "",
  clientId: "",
  status: "all"
};

const allowedStatuses: EpochStatus[] = ["all", "open", "closing", "closed"];

const epochsFilterSchema = Yup.object({
  epochFrom: Yup.string()
    .trim()
    .test("epoch-format", "Use format YYYY-MM-DD-HH.", (value) => {
      if (!value) {
        return true;
      }
      return EPOCH_ID_REGEX.test(value);
    }),
  epochTo: Yup.string()
    .trim()
    .test("epoch-format", "Use format YYYY-MM-DD-HH.", (value) => {
      if (!value) {
        return true;
      }
      return EPOCH_ID_REGEX.test(value);
    })
    .test("epoch-range", "Epoch To must be greater than or equal to Epoch From.", function (value) {
      const { epochFrom } = this.parent as EpochsFetchFilters;
      if (!epochFrom || !value) {
        return true;
      }
      return epochFrom <= value;
    }),
  supplierId: Yup.string()
    .trim()
    .test("supplier-id-format", "Supplier ID must be a valid UUID.", (value) => {
      if (!value) {
        return true;
      }
      return validateUUID(value);
    }),
  clientId: Yup.string()
    .trim()
    .test("client-id-format", "Client ID must be a valid UUID.", (value) => {
      if (!value) {
        return true;
      }
      return validateUUID(value);
    }),
  status: Yup.mixed<EpochStatus>()
    .oneOf(allowedStatuses, "Please choose a valid status.")
    .required("Please choose a valid status.")
});

export const useEpochsStore = create<EpochsStoreState>((set, get) => ({
  filters: DEFAULT_FILTERS,
  appliedFilters: DEFAULT_FILTERS,
  summary: undefined,
  epochs: [],
  isLoading: false,
  isEpochDetailsLoading: false,
  error: undefined,
  epochDetailsError: undefined,
  epochDetailsNotFoundById: {},
  epochDetailsById: {},
  validationErrors: {},
  hasFetched: false,
  setField: (field, value) => {
    set((state) => ({
      filters: {
        ...state.filters,
        [field]: value
      },
      validationErrors: {
        ...state.validationErrors,
        [field]: undefined
      }
    }));
  },
  resetFilters: () => {
    set({
      filters: DEFAULT_FILTERS,
      appliedFilters: DEFAULT_FILTERS,
      validationErrors: {},
      error: undefined,
      summary: undefined,
      epochs: [],
      hasFetched: false
    });
  },
  fetchEpochsRequest: async (inputFilters) => {
    const page = typeof inputFilters.page === "number" ? inputFilters.page : 1;
    const limit = typeof inputFilters.limit === "number" ? inputFilters.limit : 25;
    const epochFrom = normaliseEpochFilterValue(inputFilters.epochFrom);
    const epochTo = normaliseEpochFilterValue(inputFilters.epochTo);
    const supplierId = normaliseStringFilterValue(inputFilters.supplierId);
    const clientId = normaliseStringFilterValue(inputFilters.clientId);

    const response = await executeAPIRequest<EpochsApiResponseType>({
      method: "get",
      url: `/io-explorer/tokenomics/epochs?${stringify({
        epoch_from: epochFrom,
        epoch_to: epochTo,
        supplier_id: supplierId,
        client_id: clientId,
        status: inputFilters.status === "all" ? undefined : inputFilters.status,
        page,
        page_size: limit
      })}`
    });

    return normaliseEpochsResponse(response);
  },
  fetchEpochs: async () => {
    const { filters, fetchEpochsRequest } = get();
    const validationErrors = await validateFilters(filters);

    if (Object.keys(validationErrors).length > 0) {
      set({
        validationErrors,
        hasFetched: true,
        error: new Error("Please fix the highlighted fields.")
      });
      return undefined;
    }

    set({
      isLoading: true,
      error: undefined,
      validationErrors: {},
      appliedFilters: filters
    });

    try {
      const normalizedResponse = await fetchEpochsRequest(filters);

      set({
        summary: normalizedResponse.summary,
        epochs: normalizedResponse.epochs,
        hasFetched: true,
        error: undefined
      });

      return normalizedResponse;
    } catch (e) {
      set({
        hasFetched: true,
        summary: undefined,
        epochs: [],
        error: toFriendlyError(e)
      });
      return undefined;
    } finally {
      set({ isLoading: false });
    }
  },
  fetchEpochDetails: async (epochId) => {
    if (!epochId) {
      set({
        epochDetailsError: new Error("Epoch ID is missing."),
        isEpochDetailsLoading: false
      });
      return undefined;
    }

    const cachedEpochDetails = get().epochDetailsById[epochId];
    if (cachedEpochDetails?.status?.toLowerCase() === "closed") {
      set((state) => ({
        epochDetailsError: undefined,
        isEpochDetailsLoading: false,
        epochDetailsNotFoundById: {
          ...state.epochDetailsNotFoundById,
          [epochId]: false
        }
      }));
      return cachedEpochDetails;
    }

    set((state) => ({
      isEpochDetailsLoading: true,
      epochDetailsError: undefined,
      epochDetailsNotFoundById: {
        ...state.epochDetailsNotFoundById,
        [epochId]: false
      }
    }));

    try {
      const response = await executeAPIRequest<EpochDetailsResponse>({
        method: "get",
        url: `/io-explorer/tokenomics/epochs/${encodeURIComponent(epochId)}`
      });
      const normalisedDetails = normaliseEpochDetailsResponse(response?.data, epochId);

      set((state) => ({
        epochDetailsById: {
          ...state.epochDetailsById,
          [epochId]: normalisedDetails
        },
        epochDetailsError: undefined,
        epochDetailsNotFoundById: {
          ...state.epochDetailsNotFoundById,
          [epochId]: false
        }
      }));

      return normalisedDetails;
    } catch (e) {
      if (e instanceof AxiosError && e.response?.status === 404) {
        set((state) => {
          const otherEpochDetails = { ...state.epochDetailsById };
          delete otherEpochDetails[epochId];
          return {
            epochDetailsById: otherEpochDetails,
            epochDetailsError: undefined,
            epochDetailsNotFoundById: {
              ...state.epochDetailsNotFoundById,
              [epochId]: true
            }
          };
        });

        return undefined;
      }

      set({
        epochDetailsError: toFriendlyError(e),
        epochDetailsNotFoundById: {
          ...get().epochDetailsNotFoundById,
          [epochId]: false
        }
      });
      return undefined;
    } finally {
      set({ isEpochDetailsLoading: false });
    }
  }
}));

const validateFilters = async (filters: EpochsFetchFilters) => {
  try {
    await epochsFilterSchema.validate(filters, {
      abortEarly: false
    });
    return {};
  } catch (validationError) {
    const errors: Partial<Record<keyof EpochsFetchFilters, string>> = {};
    if (!(validationError instanceof Yup.ValidationError)) {
      return errors;
    }

    const allErrors = validationError.inner.length ? validationError.inner : [validationError];

    allErrors.forEach((error) => {
      const field = error.path as keyof EpochsFetchFilters | undefined;
      if (!field || errors[field] || !error.message) {
        return;
      }
      errors[field] = error.message;
    });

    return errors;
  }
};

const normaliseEpochsResponse = (response: EpochsApiResponseType): EpochsFetchResponseType => {
  const data: EpochsDataResponseType | undefined =
    response?.data || (response as unknown as EpochsDataResponseType);
  const summaryResponse: EpochSummaryResponseType | undefined = data?.summary;
  const epochsResponse = Array.isArray(data?.epochs) ? data.epochs : [];
  const totalItemsFromPagination = safeOptionalNumber(response?.pagination?.total_items);
  const totalItemsFromSummary = safeOptionalNumber(summaryResponse?.total_epochs);

  return {
    status: response?.status || "failed",
    summary: normaliseSummary(summaryResponse),
    epochs: epochsResponse.map((item) => normaliseEpoch(item)),
    resultCount: totalItemsFromPagination ?? totalItemsFromSummary ?? epochsResponse.length
  };
};

const normaliseSummary = (summary?: EpochSummaryResponseType): EpochSummaryType => {
  return {
    totalEpochs: safeNumber(summary?.total_epochs),
    totalDeviceHours: safeNumber(summary?.total_device_hours),
    totalBurn: safeNumber(summary?.total_burn),
    epochRewards: safeNumber(summary?.epoch_rewards),
    ioPurchased: safeNumber(summary?.io_purchased),
    overallTotalBurn: safeNumber(summary?.overall_total_burn)
  };
};

const normaliseEpoch = (epoch: EpochResponseType): EpochType => {
  const epochId = epoch?.epoch_id || "";
  return {
    status: epoch?.status || "unknown",
    epochId,
    start: epoch?.start || "",
    finish: epoch?.finish || "",
    devices: safeNumber(epoch?.devices),
    gpus: safeNumber(epoch?.gpus),
    epochRewards: safeNumber(epoch?.epoch_rewards),
    ioRepurchased: safeNumber(epoch?.io_repurchased),
    supplierDebtPaid: safeNumber(epoch?.supplier_debt_paid),
    supplierDebtPending: safeNumber(epoch?.supplier_debt_pending),
    epochSignature: epoch?.epoch_signature,
    burnAmount: safeNumber(epoch?.burn_amount),
    href: epochId ? `/explorer/epochs/${epochId}` : undefined
  };
};

const normaliseEpochDetailsResponse = (
  data: EpochDetailsResponse["data"] | undefined,
  epochId: string
): EpochDetails => {
  const details: EpochDetailsPayload | undefined = data?.details;
  const deviceSplit: EpochDeviceSplit | undefined = normaliseEpochDeviceSplit(data?.device_split);
  const rewardRecords = normaliseEpochRewardRecords(data?.reward_records);
  const supplierPayoutRecords = normaliseEpochSupplierPayoutRecords(data?.supplier_payout_records);
  const protocolFeeTransaction = normaliseEpochTransactionRecord(data?.protocol_fee_transaction);
  const burnTransaction = normaliseEpochTransactionRecord(data?.burn_transaction);
  const burnAmount = safeNumber(details?.burn_amount);
  const gpus = safeOptionalNumber(details?.gpus);
  const activeGpus = safeOptionalNumber(details?.active_gpus);
  return {
    epochNumber: details?.epoch_number || epochId,
    startedAt: details?.started_at || "",
    closedAt: details?.closed_at || "",
    status: details?.status || "unknown",
    activeDevices: safeOptionalNumber(details?.active_devices),
    supplierEarningsUsd: safeOptionalNumber(details?.supplier_earnings_usd),
    epochRewardsIo: safeOptionalNumber(details?.epoch_rewards_io),
    epochRewardsUsd: safeOptionalNumber(details?.epoch_rewards_usd),
    repurchasedIoAmount: safeOptionalNumber(details?.repurchased_io_amount),
    repurchasedUsdAmount: safeOptionalNumber(details?.repurchased_usd_amount),
    ioPaidOut: safeOptionalNumber(details?.io_paid_out),
    usdPaidOut: safeOptionalNumber(details?.usd_paid_out),
    openDebt: safeOptionalNumber(details?.open_debt),
    deviceSplit,
    rewardRecords,
    supplierPayoutRecords,
    protocolFeeTransaction,
    burnTransaction,
    epochSignature: details?.epoch_signature,
    gpus,
    activeGpus,
    burnAmount,
    burnTxnHash:
      typeof details?.burn_transaction === "string" ? details.burn_transaction : undefined
  };
};

const normaliseEpochDeviceSplit = (
  deviceSplit:
    | {
        device_count?: number;
        models?: {
          model_name?: string;
          share?: number;
          device_count?: number;
          gpu_count?: number;
          gpus_per_device?: number;
          supplier_count?: number;
        }[];
      }
    | undefined
): EpochDeviceSplit | undefined => {
  const modelsResponse = Array.isArray(deviceSplit?.models) ? deviceSplit.models : [];

  const models = modelsResponse
    .map((model) => {
      const rawDeviceCount = safeNumber(model?.device_count);

      return {
        modelName: typeof model?.model_name === "string" ? model.model_name : "",
        deviceCount: clampNumber(rawDeviceCount, 0),
        gpuPerDevice: Math.max(0, Math.round(toFiniteNumber(model?.gpus_per_device))),
        gpuCount: model?.gpu_count == null ? undefined : sanitiseDeviceCount(model.gpu_count),
        supplierCount: sanitiseDeviceCount(model?.supplier_count)
      };
    })
    .filter((model) => model.modelName && model.deviceCount > 0);

  const totalFromModels = models.reduce((sum, model) => sum + model.deviceCount, 0);
  const responseDeviceCount = safeNumber(deviceSplit?.device_count);
  const deviceCount = responseDeviceCount > 0 ? responseDeviceCount : totalFromModels;

  if (deviceCount <= 0 || models.length === 0) {
    return undefined;
  }

  return {
    deviceCount,
    models
  };
};

const normaliseEpochRewardRecords = (
  rewardRecords:
    | {
        device_id?: string;
        supplier_id?: string;
        model?: string;
        reward?: number;
        tx_id?: string | null;
      }[]
    | undefined
): EpochRewardRecord[] => {
  const rewardsResponse = Array.isArray(rewardRecords) ? rewardRecords : [];

  return rewardsResponse.map((record) => ({
    deviceId: typeof record?.device_id === "string" ? record.device_id : "",
    supplierId: typeof record?.supplier_id === "string" ? record.supplier_id : "",
    model: typeof record?.model === "string" ? record.model : "",
    reward: safeOptionalNumber(record?.reward),
    txId: typeof record?.tx_id === "string" ? record.tx_id : undefined
  }));
};

const normaliseEpochSupplierPayoutRecords = (
  payoutRecords:
    | {
        supplier_id?: string;
        client_id?: string;
        cluster_id?: string;
        transaction_type?: string;
        amount_usd?: number;
        amount_io?: number | null;
        tx_id?: string | null;
      }[]
    | undefined
): EpochSupplierPayoutRecord[] => {
  const recordsResponse = Array.isArray(payoutRecords) ? payoutRecords : [];

  return recordsResponse.map((record) => ({
    supplierId: typeof record?.supplier_id === "string" ? record.supplier_id : "",
    clientId: typeof record?.client_id === "string" ? record.client_id : "",
    clusterId: typeof record?.cluster_id === "string" ? record.cluster_id : "",
    transactionType: typeof record?.transaction_type === "string" ? record.transaction_type : "",
    amountUsd: safeOptionalNumber(record?.amount_usd),
    amountIo: safeOptionalNumber(record?.amount_io),
    txId: typeof record?.tx_id === "string" ? record.tx_id : undefined
  }));
};

const normaliseEpochTransactionRecord = (
  transaction:
    | {
        epoch_id?: string;
        amount_io?: number | null;
        tx_id?: string | null;
      }
    | null
    | undefined
): EpochTransactionRecord | undefined => {
  if (!transaction) {
    return undefined;
  }

  const epochId = typeof transaction.epoch_id === "string" ? transaction.epoch_id : "";
  const amountIo = safeOptionalNumber(transaction.amount_io);
  const txId = typeof transaction.tx_id === "string" ? transaction.tx_id : undefined;

  if (!epochId && amountIo === undefined && !txId) {
    return undefined;
  }

  return {
    epochId,
    amountIo,
    txId
  };
};

const safeNumber = (value: unknown) => {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
};

const clampNumber = (value: number, min: number, max = Number.POSITIVE_INFINITY) => {
  return Math.min(max, Math.max(min, value));
};

const normaliseStringFilterValue = (value: string | null | undefined) => {
  if (typeof value !== "string") {
    return undefined;
  }

  const trimmedValue = value.trim();
  return trimmedValue.length > 0 ? trimmedValue : undefined;
};

const normaliseEpochFilterValue = (value: string | null | undefined) => {
  const normalisedValue = normaliseStringFilterValue(value);
  if (!normalisedValue) {
    return undefined;
  }

  return EPOCH_ID_REGEX.test(normalisedValue) ? normalisedValue : undefined;
};

export const fetchEpochsListRequest = async (options: EpochsFetchRequestType) => {
  const response = await useEpochsStore.getState().fetchEpochsRequest(options);

  return {
    results: response.epochs,
    resultCount: response.resultCount
  };
};

const safeOptionalNumber = (value: unknown) => {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
};
